import { Badge, Button, Divider, Group, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { format, parseISO } from 'date-fns';
import { Topic } from '../../types/TopicModels';
import { primaryColor } from '../../constants/theme';

const DAY_LABELS: Record<number, string> = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun',
};

interface ReviewTopicSlot {
  topic: Topic;
  slotsPerDay: number;
  totalSlots: number;
}

interface ReviewAvailabilityModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  topicSlots: ReviewTopicSlot[];
  grandTotalSlots: number;
  startTime: string;
  endTime: string;
  activeDateTab: string | null;
  specificDates: string[];
  recurringDays: Set<number>;
  recurringEndDate: string;
}

export const ReviewAvailabilityModal = ({
  opened,
  onClose,
  onConfirm,
  isSubmitting = false,
  topicSlots,
  grandTotalSlots,
  startTime,
  endTime,
  activeDateTab,
  specificDates,
  recurringDays,
  recurringEndDate,
}: ReviewAvailabilityModalProps) => {
  const recurringDescription = (() => {
    if (recurringDays.size === 0) return 'No days selected';
    const dayLabels = [...recurringDays].sort().map((d) => DAY_LABELS[d]).join(' & ');
    return `Every ${dayLabels} until ${recurringEndDate}`;
  })();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      padding="lg"
      title={
        <Text fw={600} size="h4" ta="left">
          Review Availability
        </Text>
      }
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={5} ta="left" fw="normal">
            Topics &amp; Slots
          </Title>

          <Stack gap="sm">
            {topicSlots.map(({ topic, slotsPerDay }) => {
              return (
                <Paper
                  key={topic.id}
                  withBorder
                  shadow="none"
                  radius="sm"
                  p="sm"
                  data-testid={`review-topic-${topic.id}`}
                >
                  <Group justify="space-between" align="center">
                    <Group gap={6}>
                      <Text fw={600} size="sm">
                        {topic.title}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {topic.durationMinutes ?? 30} mins
                      </Text>
                    </Group>
                    <Text fw={700} size="sm" c={primaryColor} data-testid={`review-total-slots-${topic.id}`}>
                      {slotsPerDay} slots
                    </Text>
                  </Group>
                </Paper>
              );
            })}
          </Stack>

          <Text size="xs" c="dimmed" ta="left">Slots shown are per selected day</Text>

          <Paper
            withBorder
            shadow="none"
            radius="sm"
            p="sm"
            style={(theme) => ({ background: theme.colors.green[0], borderColor: theme.colors.green[7] })}
            data-testid="review-grand-total"
          >
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm">Time Slots</Text>
              <Text fw={700} size="sm" c={primaryColor} data-testid="review-grand-total-count">
                {grandTotalSlots} slots
              </Text>
            </Group>
          </Paper>
        </Stack>

        <Divider />

        <Stack gap={4}>
          <Title order={5} ta="left" fw="normal">Time</Title>
          <Text size="sm" c="dimmed" ta="left" data-testid="review-time-range">
            {startTime} - {endTime}
          </Text>
        </Stack>

        <Stack gap={4}>
          <Title order={5} ta="left" fw="normal">Dates</Title>
          {activeDateTab === 'recurring' ? (
            <Text size="sm" c="dimmed" ta="left" data-testid="review-recurring-description">
              {recurringDescription}
            </Text>
          ) : (
            <Stack gap="xs">
              {specificDates.length === 0 ? (
                <Text size="sm" c="dimmed">No dates selected</Text>
              ) : (
                <>
                  <Text size="xs" c="dimmed" ta="left" data-testid="review-specific-dates-count">
                    {specificDates.length} {specificDates.length === 1 ? 'date' : 'dates'} selected
                  </Text>
                  <Group gap={6} wrap="wrap">
                    {specificDates.map((iso) => (
                      <Badge
                        key={iso}
                        color="gray"
                        variant="light"
                        radius="xl"
                        size="md"
                      >
                        {format(parseISO(iso), 'EEE d MMM')}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}
            </Stack>
          )}
        </Stack>

        <Group grow gap="md">
          <Button
            color={primaryColor}
            radius="md"
            size="md"
            onClick={onConfirm}
            loading={isSubmitting}
            disabled={isSubmitting}
            data-testid="review-confirm-button"
          >
            Set Availability
          </Button>
          <Button
            variant="outline"
            color={primaryColor}
            radius="md"
            size="md"
            onClick={onClose}
            styles={{ root: { borderWidth: 1 } }}
            data-testid="review-edit-button"
          >
            Edit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
